import React, { useEffect, useState, memo } from 'react'
import { useSelector } from 'APP/Hooks/redux'
import { Linking, View } from 'react-native'
import Moment from 'moment-timezone'
import * as AddCalendarEvent from 'react-native-add-calendar-event'
import styled from 'styled-components/native'

import Card from 'APP/Components/Chat/Card'
import TextMessage from 'APP/Components/Chat/TextMessage'
import { Choice } from 'APP/Components/Intake/IntakeSingleChoice/types'
import Config from 'APP/Config'
import Alert from 'APP/Converse/Alert'
import { isIOS, isMobile, isWeb } from 'APP/Helpers/checkPlatform'
import { createIcsFile, parseIcsUrl } from 'APP/Helpers/IcsUtils'
import { useShareDeepLinkFromConfig } from 'APP/Hooks/Share'
import { getBrandConfig } from 'APP/Lib/ConfigHelpers'
import { usePostContext } from 'APP/Lib/Context'
import { logger } from 'APP/Lib/Datadog'
import { selectPreferredLanguage } from 'APP/Redux/PatientRedux'
import { checkCalendarState, requestCalendarPermission } from 'APP/NativeModules/PermissionsService'
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'

const Container = styled(View)`
  flex: 1 1 0%;
`

const AddToCalendar = () => {
  const { post } = usePostContext()
  const [permissions, setPermissions] = useState('')
  const enableCalendarDeeplink = isIOS()
  const preferredLanguage = useSelector(selectPreferredLanguage)

  useEffect(() => {
    if (isMobile()) {
      checkCalendarState()?.then((res) => setPermissions(res))
    }
  }, [])

  const episodeLink = useShareDeepLinkFromConfig(
    'ADD_TO_CALENDAR_EPISODE_DEEP_LINK_URL',
    { chat: post?.channel_id },
    { analyticsName: 'upcomingAppointmentCard.addToCalendar.episodeLink' }
  )

  if (!post) return null

  const onPress = (choice: Choice) => {
    Analytics.trackEvent('button_click', { button_value: 'Add to calendar' })
    try {
      const onError = () => {
        Alert.alert(I18n.t('CannotAddToCalendar.title'), I18n.t('CannotAddToCalendar.body'), [
          { text: I18n.t('CannotAddToCalendar.ok') },
        ])
      }

      const { title, startDate, endDate, description } = parseIcsUrl(choice.link)
      // use patient preferred lang over device lang since the title and description are sent from BM
      const { productName } = getBrandConfig(Config.BRAND_ID, preferredLanguage)
      const brandedTitle = `${productName} - ${title}`
      const joinLink = I18n.t('Appointments.calendarJoinLink', {
        episodeLink: episodeLink.url,
        lng: preferredLanguage,
      })
      const notes = enableCalendarDeeplink ? `${joinLink}${description}` : description

      if (isWeb()) {
        const formattedStartDate = Moment.utc(startDate)
        const formattedEndDate = Moment.utc(endDate)
        createIcsFile(brandedTitle, notes, formattedStartDate, formattedEndDate)
          .then((result) => Linking.openURL(result))
          .catch(onError)
      } else {
        const eventConfig = {
          title: brandedTitle,
          startDate,
          endDate,
          notes,
        }
        if (permissions === 'granted') {
          AddCalendarEvent.presentEventCreatingDialog(eventConfig).catch(onError)
        } else {
          requestCalendarPermission()?.then(() => {
            // if null or denied, will prompt for permission
            // if blocked, will show error
            AddCalendarEvent.presentEventCreatingDialog(eventConfig).catch(onError)
          })
        }
      }
    } catch (err) {
      logger.error('Failed to add appointment to calendar', {}, err)
    }
  }

  return (
    <Container>
      <TextMessage />
      <View>
        {(post.props?.choices || [])
          .filter((choice) => choice.link !== undefined)
          .map((choice) => {
            return (
              <Card
                key={choice.id}
                title={choice.display}
                iconName="calendar-blank"
                onPress={() => onPress(choice)}
                testID="CALENDAR_CARD"
              />
            )
          })}
      </View>
    </Container>
  )
}

export default memo(AddToCalendar)
